import { timing, variantForLevel, } from './params';
const FEEDBACK_MS = timing.feedbackMs;
const TICK_MS = 100;
const DIGITS = '123456789'.split('').concat(Array.from({ length: 16 }, (_, i) => String(10 + i)));
const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
const SYMBOLS = ['*', '+', 'o', 'x', 'v', '<', '>', '=', '#', '~', '?', '%', '&'];
/** Mulberry32 PRNG seeded — replay deterministe utile pour MakeStimulus. */
function makeRng(seed) {
    let s = seed >>> 0;
    return () => {
        s = (s + 0x6d2b79f5) >>> 0;
        let t = s;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
function buildLabels(mode, n) {
    if (mode === 'A') {
        return Array.from({ length: n }, (_, i) => DIGITS[i] ?? String(i + 1));
    }
    if (mode === 'B') {
        // 1 A 2 B 3 C ... — indice pair = digit, impair = lettre.
        const out = [];
        let dIdx = 0;
        let lIdx = 0;
        for (let i = 0; i < n; i += 1) {
            if (i % 2 === 0) {
                out.push(DIGITS[dIdx] ?? String(dIdx + 1));
                dIdx += 1;
            }
            else {
                out.push(LETTERS[lIdx] ?? '?');
                lIdx += 1;
            }
        }
        return out;
    }
    // mode C : 1 A * 2 B + 3 C o ...
    const out = [];
    let dIdx = 0;
    let lIdx = 0;
    let sIdx = 0;
    for (let i = 0; i < n; i += 1) {
        const slot = i % 3;
        if (slot === 0) {
            out.push(DIGITS[dIdx] ?? String(dIdx + 1));
            dIdx += 1;
        }
        else if (slot === 1) {
            out.push(LETTERS[lIdx] ?? '?');
            lIdx += 1;
        }
        else {
            out.push(SYMBOLS[sIdx] ?? '*');
            sIdx += 1;
        }
    }
    return out;
}
function placeNodes(rng, labels, dispersion) {
    // dispersion in [0..1] : margin = 0.5 * (1 - dispersion) means low dispersion clusters near center.
    const margin = Math.max(0.05, 0.5 * (1 - dispersion));
    const lo = margin;
    const hi = 1 - margin;
    // Min distance entre nodes (eviter overlap).
    const minDist = 0.12;
    const maxAttempts = 200;
    const placed = [];
    const result = [];
    for (let i = 0; i < labels.length; i += 1) {
        let x = lo + rng() * (hi - lo);
        let y = lo + rng() * (hi - lo);
        let attempts = 0;
        while (attempts < maxAttempts &&
            placed.some((p) => Math.hypot(p.x - x, p.y - y) < minDist)) {
            x = lo + rng() * (hi - lo);
            y = lo + rng() * (hi - lo);
            attempts += 1;
        }
        placed.push({ x, y });
        result.push({
            id: `n${i}`,
            label: labels[i] ?? String(i + 1),
            x,
            y,
            seqIdx: i,
        });
    }
    return result;
}
export function makeStimulus(trialIdx, value, level) {
    const variant = variantForLevel(level);
    const nItems = Math.max(5, Math.min(25, Math.round(value)));
    const labels = buildLabels(variant.mode, nItems);
    // Seed RNG par (trialIdx, level) pour replay deterministe.
    const seed = (trialIdx + 1) * 9973 + level * 31;
    const rng = makeRng(seed);
    const nodes = placeNodes(rng, labels, variant.dispersion);
    return {
        trialIdx,
        mode: variant.mode,
        nodes,
        nItems,
        deadlineMs: variant.deadlineMs,
    };
}
export function createLogicTrailAdapter(level = 1) {
    let state = {
        subPhase: 'idle',
        stim: null,
        expectedSeqIdx: 0,
        tapOrder: [],
        errors: 0,
        remainingMs: 0,
        feedback: null,
        level,
        variant: variantForLevel(level),
    };
    const listeners = new Set();
    let resolver = null;
    let rejecter = null;
    let trialStart = 0;
    let tickInterval = null;
    let deadlineTimer = null;
    const timers = [];
    function clearAllTimers() {
        while (timers.length > 0) {
            const t = timers.pop();
            if (t !== undefined)
                clearTimeout(t);
        }
        if (tickInterval !== null) {
            clearInterval(tickInterval);
            tickInterval = null;
        }
        if (deadlineTimer !== null) {
            clearTimeout(deadlineTimer);
            deadlineTimer = null;
        }
    }
    function update(patch) {
        state = { ...state, ...patch };
        for (const l of listeners) {
            try {
                l(state);
            }
            catch {
                /* ignored */
            }
        }
    }
    function settle(correct) {
        if (!resolver)
            return;
        const completionTimeMs = Date.now() - trialStart;
        const resp = {
            tapOrder: [...state.tapOrder],
            errors: state.errors,
            completionTimeMs,
            correct,
            reactionTimeMs: completionTimeMs,
        };
        const r = resolver;
        resolver = null;
        rejecter = null;
        if (tickInterval !== null) {
            clearInterval(tickInterval);
            tickInterval = null;
        }
        if (deadlineTimer !== null) {
            clearTimeout(deadlineTimer);
            deadlineTimer = null;
        }
        r(resp);
    }
    return {
        mount() { },
        unmount() {
            clearAllTimers();
            listeners.clear();
            if (rejecter) {
                rejecter(new Error('unmount'));
                rejecter = null;
                resolver = null;
            }
        },
        presentStimulus(stim) {
            clearAllTimers();
            return new Promise((resolve, reject) => {
                resolver = resolve;
                rejecter = reject;
                update({
                    subPhase: 'fixation',
                    stim,
                    expectedSeqIdx: 0,
                    tapOrder: [],
                    errors: 0,
                    remainingMs: stim.deadlineMs,
                    feedback: null,
                });
                timers.push(setTimeout(() => {
                    update({ subPhase: 'trail' });
                    trialStart = Date.now();
                    // Tick countdown.
                    tickInterval = setInterval(() => {
                        if (state.subPhase !== 'trail')
                            return;
                        const elapsed = Date.now() - trialStart;
                        const remaining = Math.max(0, stim.deadlineMs - elapsed);
                        update({ remainingMs: remaining });
                    }, TICK_MS);
                    // Hard deadline.
                    deadlineTimer = setTimeout(() => {
                        if (state.subPhase === 'trail') {
                            settle(false);
                        }
                    }, stim.deadlineMs);
                }, timing.fixationMs));
            });
        },
        async showFeedback(correct) {
            update({
                subPhase: 'feedback',
                feedback: correct ? 'correct' : 'incorrect',
            });
            await new Promise((resolve) => {
                timers.push(setTimeout(() => {
                    update({ subPhase: 'iti' });
                    resolve();
                }, FEEDBACK_MS));
            });
        },
        pause() { },
        resume() { },
        abort() {
            clearAllTimers();
            if (rejecter) {
                rejecter(new Error('aborted'));
                rejecter = null;
                resolver = null;
            }
        },
        getState() {
            return state;
        },
        subscribe(listener) {
            listeners.add(listener);
            listener(state);
            return () => {
                listeners.delete(listener);
            };
        },
        handleNodeTap(id) {
            if (state.subPhase !== 'trail' || !state.stim)
                return;
            const expected = state.stim.nodes[state.expectedSeqIdx];
            if (!expected)
                return;
            const newTapOrder = [...state.tapOrder, id];
            if (id === expected.id) {
                const nextIdx = state.expectedSeqIdx + 1;
                update({
                    expectedSeqIdx: nextIdx,
                    tapOrder: newTapOrder,
                });
                if (nextIdx >= state.stim.nodes.length) {
                    // Termine. Correct = aucune erreur.
                    const ok = state.errors === 0;
                    settle(ok);
                }
            }
            else {
                update({
                    tapOrder: newTapOrder,
                    errors: state.errors + 1,
                });
            }
        },
        setLevel(newLevel) {
            const v = variantForLevel(newLevel);
            update({ level: newLevel, variant: v });
        },
    };
}
//# sourceMappingURL=adapter.js.map